/*    
<summary>
  This functional component "AddShiftManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-Jan-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { getHoursDifferenceFromNow } from "../../../../constants/constants";
import { CustomerTypeEnum } from "../../../../constants/enums/customer-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { IAddCustomer } from "../../../../models/forms/IAddCustomerManagement";
import { formatMessage } from "../../../../translations/format-message";
import CreateWalkInCustomerAccountForm from "../forms/create-walk-in-customer-account-form";
import LoginWalkInCustomerAccountForm from "../forms/login-walk-in-customer-account-form";
import SignUpCustomerForm from "../forms/sign-up-customer-form";
import ConfirmPopup from "../../../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../../../shared-components/popup/confirm/confirm-popup-hoc";
import { AuthType } from "../../../../constants/enums/auth-type-enum";
import RoutesConstants from "../../../../constants/routes.constant";
import { useHistory } from "react-router-dom";

interface IProps {
  appointmentData: any;
  storeId: number;
  modalClosed: () => void;
  businessTypeId: number;
  showConfirmPopup: boolean;
  confirmPopupToggleHandler: () => void;
}

const AuthenticateCustomerAccount = (props: IProps) => {
  const {
    bookingManagementStore,
    masterManagementStore,
    customerManagementStore,
    authStore,
  } = useStore();
  const {
    AuthenticateCustomer,
    authenticateCustomerState,
    addUpdateAppointmentState,
    updateWalkInBookingData,
    AddWalkInCustomerAppointmentService,
    AddCustomerRegister,
    addUpdateCustomerState,
    customerDetail,
    resetCustomerDetail,
    inProgress,
    customerState,
    resetAddUpdateCustomer,
    setAppointmentType
  } = bookingManagementStore;
  const {
    authenticateCustomerAuthCodeState,
    generateCustomerAuthCodeState,
    GenerateCustomerAuthCodeService,
    resetGenerateCustomerAuthCodeState,
    resetAuthenticateCustomerAuthCode,
    setIsCustomerAuthCodeValidatedFalse,
    ResetPasswordAuthLinkService,
    resetPasswordAuthLinkState,
    resetResetPassowrdAuthLinkState,
    isResetPasswordAuthLinkGenerated,
    resetIsResetPasswordAuthLinkGenerated,
    AppointmentRegisterCustomerlogin
  } = authStore;
  const { isCustomerValidated, customerValidatedData } =
    customerManagementStore;
  const history = useHistory();
  const { getReservationSettingDetail } = masterManagementStore;
  const [currentFormNo, setFormNo] = useState(1);
  const [values, setTempValues] = useState<any>(null);
  const createWalkInCustomerInitialValues = {
    FirstNameKana: "",
    LastNameKana: "",
    Gender: "2",
    Email: "",
    Phone1: "",
    CustomerType: 0, // UnRegistered
    RegisteredBy: 1, // DSHS
  };

  const initialValues: IAddCustomer = {
    ...customerDetail,
    Password: customerDetail?.Id ? "*********" : "",
  };

  const loginWalkInCustomerInitialValues = {
    email: "",
    password: "",
  };

  /**
   * This function adds the staff when "Id < 0" and updates the Existing staff when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitLoginForm = (values: any) => {
    if (
      getHoursDifferenceFromNow(
        moment(props.appointmentData?.Date).format("YYYY-MM-DD"),
        props.appointmentData.Time
      ) < getReservationSettingDetail?.NewReservationTime
    ) {
        let errorMsg = formatMessage("you_can_add_reservation_prior_to_hour");
        toast.error(errorMsg.replace("{date}", getReservationSettingDetail?.NewReservationTime));
        // Show error if time difference is less than allowed
    } else {
      AuthenticateCustomer(values, {
        ...props.appointmentData,
        BusinessTypeId: props.businessTypeId,
      });
      setAppointmentType(1);
    }
  };

  const registerAndLoginCustomer = (data: any) =>{
    AppointmentRegisterCustomerlogin({
      Email: data.Email,
      Password: data.Password
    })
  }

  const onSubmitForm = (values: any) => {
    if (
      getHoursDifferenceFromNow(
        moment(props.appointmentData?.Date).format("YYYY-MM-DD"),
        props.appointmentData.Time
      ) < getReservationSettingDetail?.NewReservationTime
    ) {
        let errorMsg = formatMessage("you_can_add_reservation_prior_to_hour");
        toast.error(errorMsg.replace("{date}", getReservationSettingDetail?.NewReservationTime));
        // Show error if time difference is less than allowed
    } else {
      let tempValues = cloneDeep(values);
      AddCustomerRegister(
        { ...tempValues, StoreId: props.storeId, CustomerType: CustomerTypeEnum.Registered },
        {
          ...props.appointmentData,
          BusinessTypeId: props.businessTypeId,
        },
        registerAndLoginCustomer
      );
      setAppointmentType(3);
    }
  };

  const onSubmitOverwriteForm = (valuesData:any) => {
    if (
      getHoursDifferenceFromNow(
        moment(props.appointmentData?.Date).format("YYYY-MM-DD"),
        props.appointmentData.Time
      ) < getReservationSettingDetail?.NewReservationTime
    ) {
        let errorMsg = formatMessage("you_can_add_reservation_prior_to_hour");
        toast.error(errorMsg.replace("{date}", getReservationSettingDetail?.NewReservationTime));
        // Show error if time difference is less than allowed
    } else {
      let tempValues = cloneDeep(valuesData);
      tempValues.CustomerType = 0;
      setTempValues(tempValues);
      AddCustomerRegister(
        { ...tempValues, IsOverwriteEmail: true },
        {
          ...props.appointmentData,
          BusinessTypeId: props.businessTypeId,
        },
        registerAndLoginCustomer
      );
    }
  };

  const onSubmitWalkInForm = (values: any) => {
    values.Gender = Number(values.Gender);
    let tempValues = updateWalkInBookingData({
      ...props.appointmentData,
      Email: values.Email?.toLowerCase(),
      FirstNameKana: values.FirstNameKana,
      Gender: values.Gender,
      LastNameKana: values.LastNameKana,
      RegisteredBy: values.RegisteredBy,
      Phone1: values.Phone1,
      CustomerType: CustomerTypeEnum.UnRegistered,
    });

    if (
      getHoursDifferenceFromNow(
        moment(props.appointmentData?.Date).format("YYYY-MM-DD"),
        props.appointmentData?.Time
      ) < getReservationSettingDetail?.NewReservationTime
    ) {
        let errorMsg = formatMessage("you_can_add_reservation_prior_to_hour");
        toast.error(errorMsg.replace("{date}", getReservationSettingDetail?.NewReservationTime));
        // Show error if time difference is less than allowed
      } else {
      AddWalkInCustomerAppointmentService({
        BusinessTypeId: props.businessTypeId,
        ...tempValues,
      });
      setAppointmentType(2);
    }
  };

  useEffect(() => {
    if (addUpdateAppointmentState.success) {
      onModalClosed();
    }
  }, [addUpdateAppointmentState.success]);

  useEffect(() => {
    if (addUpdateAppointmentState.error) {
      onModalClosed();
    }
  }, [addUpdateAppointmentState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCustomerState.error) {
      toast.error(formatMessage(addUpdateCustomerState.error));
      resetAddUpdateCustomer();
    }
  }, [addUpdateCustomerState.error]);

  useEffect(() => {
    if (generateCustomerAuthCodeState.error) {
      toast.error(formatMessage(generateCustomerAuthCodeState.error));
      resetAddUpdateCustomer();
      resetGenerateCustomerAuthCodeState();
    }
  }, [generateCustomerAuthCodeState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateCustomerState.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateCustomerState.success) {
      toast.success(formatMessage("added_success"));
      resetAddUpdateCustomer();
    }
  }, [addUpdateCustomerState.success]);

  useEffect(() => {
    if (isCustomerValidated == true) {
      GenerateCustomerAuthCodeService({
        Email: customerValidatedData.Email,
        KanaName: customerValidatedData.KanaName,
        AuthType: customerValidatedData.AuthType,
      });
    }
  }, [isCustomerValidated]);

  /**
   * This function adds the customer when "Id < 0" and updates the Existing customer when Id > 0 by providing form values to the
   * correponding function
   */

  /**
   * This function closes the pop up modal and reset getstaffdetails, addUpdate staff observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetAuthenticateCustomerAuthCode();
    resetGenerateCustomerAuthCodeState();
    setIsCustomerAuthCodeValidatedFalse();
    props.modalClosed();
  };

  const changeForm = (formNo: any) => {
    setFormNo(formNo);
    resetAuthenticateCustomerAuthCode();
    resetGenerateCustomerAuthCodeState();
    setIsCustomerAuthCodeValidatedFalse();
  };

  useEffect(() => {
    resetCustomerDetail();
  }, []);

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "changePasswordSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (resetPasswordAuthLinkState.success) {
      // toast.success(formatMessage("password_reset_link_sent"));
      resetResetPassowrdAuthLinkState();
    }
  }, [resetPasswordAuthLinkState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "changePasswordError" of the useEffect changed.
   */
  useEffect(() => {
    if (resetPasswordAuthLinkState.error) {
      toast.error(formatMessage(resetPasswordAuthLinkState.error));
      resetResetPassowrdAuthLinkState();
    }
  }, [resetPasswordAuthLinkState.error]);

  /**
   * This function changes the user password by provides new password and user Id  to "chagePassword" store funtion that
   * send the request to Api.
   */
  const onSubmitResetLinkForm = (values: any) => {
    ResetPasswordAuthLinkService({
      Email: values.Email,
      AuthType: AuthType.PasswordResetLinkAuth,
    });
  };

  const onPasswordResetClosed = () => {
    resetIsResetPasswordAuthLinkGenerated();
    history.push(RoutesConstants.CustomerLogin);
  };

  return (
    <React.Fragment>
      <div>
        {currentFormNo === 1 ? (
          <LoginWalkInCustomerAccountForm
            initialValues={loginWalkInCustomerInitialValues}
            actionInProgress={authenticateCustomerState.inProgress}
            isResetPasswordAuthLinkGenerated={isResetPasswordAuthLinkGenerated}
            submitHandler={onSubmitLoginForm}
            onPasswordResetClosed={onPasswordResetClosed}
            submitResetLinkForm={onSubmitResetLinkForm}
            onModalClosed={onModalClosed}
            changeForm={changeForm}
          />
        ) : currentFormNo === 2 ? (
          <CreateWalkInCustomerAccountForm
            initialValues={createWalkInCustomerInitialValues}
            getCustomerLoading={addUpdateAppointmentState.inProgress}
            modalClosed={props.modalClosed}
            submitHandler={onSubmitWalkInForm}
            onSubmitOverwriteForm={onSubmitOverwriteForm}
            onModalClosed={onModalClosed}
            changeForm={changeForm}
            id={-1}
          />
        ) : (
          <React.Fragment>
            <Modal show={true} centered className="double-modal modal-md">
              <Modal.Header>
                <Modal.Title>
                  <FormattedMessage id="register_customer" />
                </Modal.Title>
                <div onClick={onModalClosed} className="Crossicons">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="23"
                    height="23"
                    fill="currentColor"
                    className="bi bi-x"
                    viewBox="0 0 16 16"
                  >
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                  </svg>
                </div>
              </Modal.Header>
              <Modal.Body>
                <SignUpCustomerForm
                  initialValues={initialValues}
                  id={-1}
                  submitHandler={onSubmitForm}
                  modalClosed= {props.modalClosed}
                  getCustomerLoading={
                    customerState.inProgress ||
                    addUpdateCustomerState.inProgress ||
                    addUpdateAppointmentState.inProgress ||
                    inProgress
                  }
                  changeForm={changeForm}
                />
              </Modal.Body>
            </Modal>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

export default ConfirmPopupHOC(observer(AuthenticateCustomerAccount));
